import { isLikelyRdTitle } from "../utils/rdTitleMatch.js";

/**
 * Demo team for a mid-size (~32 person) Canadian SaaS company.
 * Shaped to match Airtable `people` fields used in company profiles.
 * Admin-only preview until live people data is wired.
 */

const withMatch = (person) => ({
  ...person,
  matchedTitle: person.matchedTitle ?? (isLikelyRdTitle(person.title) ? person.title : null),
  isRd: isLikelyRdTitle(person.title),
});

const TEAM_MEMBERS_RAW = [
  // Leadership
  { id: "p1", name: "Alex Rivera", title: "Chief Executive Officer", salary: 220000, linkedInWebsite: "https://linkedin.com/in/demo-alex", skills: ["Leadership", "Strategy"] },
  { id: "p2", name: "Jordan Chen", title: "Chief Technology Officer", salary: 210000, linkedInWebsite: "https://linkedin.com/in/demo-jordan", skills: ["Leadership", "Technical Skills"] },
  { id: "p3", name: "Sam Okonkwo", title: "Chief Financial Officer", salary: 195000, linkedInWebsite: "https://linkedin.com/in/demo-sam", skills: ["Finance", "Leadership"] },
  { id: "p4", name: "Morgan Blake", title: "VP Engineering", salary: 185000, linkedInWebsite: "https://linkedin.com/in/demo-morgan", skills: ["Leadership", "Technical Skills"] },
  { id: "p5", name: "Riley Park", title: "VP Product", salary: 175000, linkedInWebsite: "https://linkedin.com/in/demo-riley", skills: ["Strategy", "Product"] },
  { id: "p6", name: "Casey Nguyen", title: "VP Sales", salary: 170000, linkedInWebsite: "https://linkedin.com/in/demo-casey", skills: ["Sales", "Leadership"] },

  // Engineering / R&D
  { id: "p7", name: "Taylor Brooks", title: "Staff Software Engineer", salary: 165000, skills: ["Technical Skills"] },
  { id: "p8", name: "Jamie Soto", title: "Principal Software Engineer", salary: 172000, skills: ["Technical Skills"] },
  { id: "p9", name: "Avery Kim", title: "Senior Backend Engineer", salary: 148000, skills: ["Technical Skills"] },
  { id: "p10", name: "Quinn Patel", title: "Senior Frontend Engineer", salary: 142000, skills: ["Technical Skills"] },
  { id: "p11", name: "Reese Alvarez", title: "Full Stack Developer", salary: 128000, skills: ["Technical Skills"] },
  { id: "p12", name: "Cameron Wright", title: "Software Engineer", salary: 115000, skills: ["Technical Skills"] },
  { id: "p13", name: "Harper Singh", title: "Software Engineer", salary: 112000, skills: ["Technical Skills"] },
  { id: "p14", name: "Drew Morales", title: "Platform Engineer", salary: 138000, skills: ["Technical Skills", "Operations"] },
  { id: "p15", name: "Parker Ellis", title: "DevOps Engineer", salary: 135000, skills: ["Technical Skills", "Operations"] },
  { id: "p16", name: "Skyler Amin", title: "Data Engineer", salary: 140000, skills: ["Technical Skills", "Data Analysis"] },
  { id: "p17", name: "Finley Zhao", title: "Machine Learning Engineer", salary: 155000, skills: ["Technical Skills", "Data Analysis"] },
  { id: "p18", name: "Blake Torres", title: "QA Engineer", salary: 98000, skills: ["Technical Skills"] },
  { id: "p19", name: "Jordan Lee", title: "Mobile Engineer", salary: 125000, skills: ["Technical Skills"] },
  { id: "p20", name: "Alexis Grant", title: "Product Manager", salary: 132000, skills: ["Strategy", "Communication"] },
  { id: "p21", name: "Dana Ruiz", title: "Technical Product Manager", salary: 138000, skills: ["Strategy", "Technical Skills"] },

  // GTM / Ops
  { id: "p22", name: "Chris Bailey", title: "Account Executive", salary: 110000, skills: ["Sales"] },
  { id: "p23", name: "Pat Holloway", title: "Account Executive", salary: 105000, skills: ["Sales"] },
  { id: "p24", name: "Leslie Tran", title: "Customer Success Manager", salary: 92000, skills: ["Communication"] },
  { id: "p25", name: "Shawn Ortiz", title: "Customer Success Manager", salary: 88000, skills: ["Communication"] },
  { id: "p26", name: "Nina Volkova", title: "Marketing Manager", salary: 98000, skills: ["Marketing"] },
  { id: "p27", name: "Gabe Fontaine", title: "Content Marketer", salary: 78000, skills: ["Marketing", "Communication"] },
  { id: "p28", name: "Ivy Chen", title: "People Operations Manager", salary: 95000, skills: ["Operations", "Communication"] },
  { id: "p29", name: "Omar Hassan", title: "Office Manager", salary: 68000, skills: ["Operations"] },
  { id: "p30", name: "Elena Petrova", title: "Controller", salary: 118000, skills: ["Finance"] },
  { id: "p31", name: "Marcus Webb", title: "Business Development Manager", salary: 108000, skills: ["Business Development", "Sales"] },
  { id: "p32", name: "Sofia Bergman", title: "Designer", salary: 102000, skills: ["Other"] },
];

const OPEN_ROLES_RAW = [
  { id: "j1", name: "Open role", title: "Senior Software Engineer", salary: 145000, location: "Vancouver / Remote", postedDaysAgo: 12 },
  { id: "j2", name: "Open role", title: "Machine Learning Engineer", salary: 150000, location: "Toronto / Remote", postedDaysAgo: 8 },
  { id: "j3", name: "Open role", title: "Product Designer", salary: 110000, location: "Vancouver", postedDaysAgo: 21 },
  { id: "j4", name: "Open role", title: "Account Executive", salary: 100000, location: "Remote (Canada)", postedDaysAgo: 5 },
  { id: "j5", name: "Open role", title: "Platform Engineer", salary: 140000, location: "Remote (Canada)", postedDaysAgo: 3 },
];

export const DEMO_COMPANY_TEAM = {
  companyName: "Northline Systems",
  domain: "northline.example",
  headcountLabel: "32 employees",
  members: TEAM_MEMBERS_RAW.map(withMatch),
  openings: OPEN_ROLES_RAW.map(withMatch),
};

export const RD_MIN_RATE = 0.3;
export const RD_MAX_RATE = 0.65;

export function rdMinAmount(salary) {
  return (Number(salary) || 0) * RD_MIN_RATE;
}

export function rdMaxAmount(salary) {
  return (Number(salary) || 0) * RD_MAX_RATE;
}

export function summarizeTeam(members = [], openings = []) {
  const rdMembers = members.filter((m) => m.isRd);
  const rdOpenings = openings.filter((o) => o.isRd);
  const totalPayroll = members.reduce((sum, m) => sum + (Number(m.salary) || 0), 0);
  const rdPayroll = rdMembers.reduce((sum, m) => sum + (Number(m.salary) || 0), 0);
  const openRdPayroll = rdOpenings.reduce((sum, o) => sum + (Number(o.salary) || 0), 0);
  const rdMinTotal = rdMembers.reduce((sum, m) => sum + rdMinAmount(m.salary), 0);
  const rdMaxTotal = rdMembers.reduce((sum, m) => sum + rdMaxAmount(m.salary), 0);
  const openRdMinTotal = rdOpenings.reduce((sum, o) => sum + rdMinAmount(o.salary), 0);
  const openRdMaxTotal = rdOpenings.reduce((sum, o) => sum + rdMaxAmount(o.salary), 0);

  return {
    headcount: members.length,
    rdCount: rdMembers.length,
    nonRdCount: members.length - rdMembers.length,
    rdShare: members.length ? rdMembers.length / members.length : 0,
    totalPayroll,
    rdPayroll,
    rdMinTotal,
    rdMaxTotal,
    openingsCount: openings.length,
    rdOpeningsCount: rdOpenings.length,
    openRdPayroll,
    openRdMinTotal,
    openRdMaxTotal,
  };
}
